import React from "react";
import { Lote } from "../types";
import { DollarSign, UserCheck, AlertCircle, Package } from "lucide-react";

interface LoteCardProps {
  lote: Lote;
  onClick: () => void;
  onSimulate?: () => void;
}

export const LoteCard: React.FC<LoteCardProps> = ({ lote, onClick, onSimulate }) => {
  const formatMoney = (val: number | null | undefined) => {
    const num = Number(val);
    if (val === null || val === undefined || isNaN(num)) return "N/D";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(num);
  };

  return (
    <article className="lote-card" onClick={onClick}>
      <header className="lote-card-header">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 800,
              fontSize: "1.1rem",
              color: "var(--primary)",
            }}
          >
            LOTE #{lote.nr_atribuido}
          </span>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            {lote.tipo || "DIVERSOS"}
          </span>
        </div>

        <div style={{ display: "flex", gap: "6px" }}>
          {lote.permite_pf === 1 ? (
            <span className="badge badge-pf" title="Autorizado para Pessoa Física e Jurídica">
              <UserCheck size={12} /> PF & PJ
            </span>
          ) : (
            <span className="badge badge-pj" title="Apenas Pessoa Jurídica (CNPJ)">
              PJ
            </span>
          )}

          {lote.lote_heterogeneo === 1 && (
            <span
              className="badge badge-heterogeneo"
              title="Lote heterogêneo composto por múltiplos produtos distintos. O custo unitário é médio."
            >
              <AlertCircle size={12} /> Misto
            </span>
          )}
        </div>
      </header>

      <div className="lote-card-body">
        {/* Destaque: Custo Unitário Estimado */}
        <div className="metric-highlight">
          <div className="metric-label">Custo Unitário Estimado</div>
          <div className="metric-value-huge">
            {formatMoney(lote.custo_unitario_estimado)}
          </div>
          <div className="metric-sub">
            {Number(lote.qtde_total_unidades) > 0
              ? `Calculado sobre ${(Number(lote.qtde_total_unidades) || 0).toLocaleString("pt-BR")} unidades físicas`
              : "Sem itens detalhados"}
          </div>
        </div>

        {/* Métricas do Lote */}
        <div className="metrics-row">
          <div style={{ background: "var(--bg-card)", padding: "10px", borderRadius: "10px", border: "1px solid var(--border-color)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", color: "var(--text-muted)" }}>
              <DollarSign size={14} color="#38bdf8" /> Lance Mínimo
            </div>
            <div style={{ fontSize: "1.05rem", fontWeight: 700, fontFamily: "var(--font-mono)", marginTop: "2px" }}>
              {formatMoney(lote.valor_minimo)}
            </div>
          </div>

          <div style={{ background: "var(--bg-card)", padding: "10px", borderRadius: "10px", border: "1px solid var(--border-color)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", color: lote.valor_avaliacao ? "#60a5fa" : "var(--text-muted)" }}>
              <DollarSign size={14} color={lote.valor_avaliacao ? "#60a5fa" : "#a78bfa"} /> {lote.valor_avaliacao ? "Avaliação RFB" : "Composição"}
            </div>
            <div style={{ fontSize: "0.95rem", fontWeight: 700, fontFamily: lote.valor_avaliacao ? "var(--font-mono)" : "inherit", color: lote.valor_avaliacao ? "#93c5fd" : "inherit", marginTop: "2px" }}>
              {lote.valor_avaliacao ? formatMoney(lote.valor_avaliacao) : `${lote.qtde_itens_distintos} itens`}
            </div>
          </div>
        </div>

        {/* Composição / Preview dos Itens do Lote */}
        {lote.preview_itens && lote.preview_itens.length > 0 && (
          <div
            style={{
              background: "rgba(15, 23, 42, 0.65)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
              borderRadius: "10px",
              padding: "8px 10px",
              fontSize: "0.76rem",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#94a3b8",
                fontSize: "0.72rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#38bdf8" }}>
                <Package size={13} /> Composição do Lote
              </span>
              <span style={{ color: "#cbd5e1", fontSize: "0.7rem" }}>
                {lote.qtde_itens_distintos} produtos
              </span>
            </div>

            {/* Top marcas */}
            {lote.top_marcas && lote.top_marcas.length > 0 && (
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {lote.top_marcas.map((m) => (
                  <span
                    key={m.marca}
                    style={{
                      background: "rgba(56, 189, 248, 0.12)",
                      color: "#7dd3fc",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      padding: "1px 6px",
                      borderRadius: "4px",
                      border: "1px solid rgba(56, 189, 248, 0.2)",
                    }}
                  >
                    {m.marca} ({(Number(m.total_qtd) || 0).toLocaleString("pt-BR")})
                  </span>
                ))}
              </div>
            )}

            {/* Lista dos 2 primeiros itens consolidados */}
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {(lote.preview_itens || []).slice(0, 2).map((it, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "6px",
                    color: "#e2e8f0",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "200px",
                    }}
                    title={it.descricao}
                  >
                    • {it.descricao}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontWeight: 700,
                      color: "#34d399",
                      fontSize: "0.72rem",
                      flexShrink: 0,
                    }}
                  >
                    {(Number(it.quantidade) || 0).toLocaleString("pt-BR")} {it.un_medida}
                  </span>
                </div>
              ))}
              {lote.qtde_itens_distintos > 2 && (
                <span style={{ fontSize: "0.68rem", color: "#64748b", marginTop: "1px" }}>
                  + {lote.qtde_itens_distintos - 2} outros itens detalhados...
                </span>
              )}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
          {lote.cidade ? (
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              📍 {lote.cidade}
            </div>
          ) : <div />}

          <span
            onClick={(e) => {
              e.stopPropagation();
              if (onSimulate) onSimulate();
              else onClick();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "#38bdf8",
              background: "rgba(56, 189, 248, 0.15)",
              padding: "4px 10px",
              borderRadius: "6px",
              border: "1px solid rgba(56, 189, 248, 0.3)",
              cursor: "pointer",
            }}
          >
            🎯 Simular Lance
          </span>
        </div>
      </div>
    </article>
  );
};
